import { type LucideProps, useLucideContext } from 'lucide-react';
import * as React from 'react';

import { hasA11yProp } from './hasA11yProp';
import { ICON_MARKER } from './iconMarker';

/** A Lucide icon's props, so that a custom icon can stand in for one, such as a `LucideIcon` prop's value. */
export type CustomIconProps = Omit<LucideProps, 'ref'>;

/** What a drawing paints with: the icon's props, else a LucideProvider's, else the drawing's defaults. */
export interface CustomIconPaint {
    color: string;
    /** Spread on the drawing's stroked elements; `strokeWidth` is in the drawing's units. */
    stroke: { strokeWidth: number; vectorEffect?: 'non-scaling-stroke' };
}

export interface CustomIconDrawing<OwnProps extends object, OwnColors extends boolean = false> {
    /** The drawing's width in its own units: its view box is `0 0 width height`, and the icon keeps its ratio. */
    width: number;
    /** The drawing's height in its own units, and the icon's height in px without a `size`. */
    height: number;
    /** The default color, for a drawing in a color of its own design; `currentColor` otherwise. */
    color?: string;
    /** For a drawing that paints only colors of its own: its icon then takes no `color`, which would change nothing. */
    ownColors?: OwnColors;
    /** In the drawing's units; Lucide's 2 by default. */
    strokeWidth?: number;
    /** Its own props beyond a Lucide icon's, with their defaults; they don't reach the svg. */
    props?: OwnProps;
    /** The svg's content. */
    render: (paint: CustomIconPaint, props: OwnProps) => React.ReactNode;
}

export type CustomIcon<
    OwnProps extends object = Record<never, never>,
    OwnColors extends boolean = false,
> = React.ForwardRefExoticComponent<
    (OwnColors extends true ? Omit<CustomIconProps, 'color'> : CustomIconProps) &
        Partial<OwnProps> &
        React.RefAttributes<SVGSVGElement>
>;

function pxOf(size: string | number): number | null {
    if (typeof size === 'number') {
        return size;
    }
    return /^\d+(\.\d+)?(px)?$/.test(size) ? Number.parseFloat(size) : null;
}

/** Makes an icon that behaves like a Lucide icon from a drawing Lucide doesn't have, such as a domain glyph. */
export function createCustomIcon<OwnProps extends object = Record<never, never>, OwnColors extends boolean = false>(
    displayName: string,
    {
        width,
        height,
        color: drawingColor = 'currentColor',
        strokeWidth: drawingStrokeWidth = 2,
        props: ownDefaults,
        render,
    }: CustomIconDrawing<OwnProps, OwnColors>,
): CustomIcon<OwnProps, OwnColors> {
    const ownNames = Object.keys(ownDefaults ?? {});
    const Icon = React.forwardRef<SVGSVGElement, CustomIconProps>((allProps, ref) => {
        const { size, color, strokeWidth, absoluteStrokeWidth, nonScalingStroke, className, children, ...rest } =
            allProps;

        const context = useLucideContext();
        const own: Record<string, unknown> = { ...ownDefaults };
        const svgProps: typeof rest = { ...rest };
        for (const name of ownNames) {
            const value: unknown = Reflect.get(svgProps, name);
            if (value !== undefined) {
                own[name] = value;
            }
            Reflect.deleteProperty(svgProps, name);
        }

        const iconSize = size ?? context.size ?? height;
        const px = pxOf(iconSize);
        // Without a width, the browser keeps the drawing's ratio through the view box.
        const iconWidth = width === height ? iconSize : px === null ? undefined : (px * width) / height;
        const lineWidth = Number(strokeWidth ?? context.strokeWidth ?? drawingStrokeWidth);
        const paint: CustomIconPaint = {
            color: color ?? context.color ?? drawingColor,
            stroke: {
                strokeWidth:
                    (absoluteStrokeWidth ?? context.absoluteStrokeWidth) === true && px !== null
                        ? (lineWidth * height) / px
                        : lineWidth,
                ...((nonScalingStroke ?? context.nonScalingStroke) === true && {
                    vectorEffect: 'non-scaling-stroke' as const,
                }),
            },
        };
        const classes = [context.className, className].filter(Boolean).join(' ');

        return (
            <svg
                ref={ref}
                {...ICON_MARKER}
                xmlns="http://www.w3.org/2000/svg"
                width={iconWidth}
                height={iconSize}
                viewBox={`0 0 ${width} ${height}`}
                fill="none"
                className={classes === '' ? undefined : classes}
                aria-hidden={Boolean(children) || hasA11yProp(svgProps) ? undefined : 'true'}
                {...svgProps}
            >
                {render(paint, own as OwnProps)}
                {children}
            </svg>
        );
    });
    Icon.displayName = displayName;
    // The cast adds the drawing's own props, read off above, and drops `color` for a drawing with colors of its own.
    return Icon as CustomIcon<OwnProps, OwnColors>;
}
